import React from 'react';
import { useTranslation } from 'react-i18next';
import CircularProgress from '@mui/material/CircularProgress';
import { useSelector } from 'react-redux';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import CommonButton from '@src/new-components/Button';
import Typography from '@src/new-components/Typography';
import { COLOR } from '@src/styles/color';
import TipsAndUpdatesRoundedIcon from '@mui/icons-material/TipsAndUpdatesRounded';
import ImportContactsRoundedIcon from '@mui/icons-material/ImportContactsRounded';
import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { Box } from '@mui/material';
import Tooltip from '@src/components/Tooltip';
import {
  StyledFooterActions,
  LeftActions,
  RightActions,
  AutoSaveWrapper,
} from './index.style';

const FooterActions = ({
  handleOpenTipsList,
  onGuideClick,
  onContinue,
  continueDisabled = false,
  continueTooltip = '',
  showAutoSave = false,
  autoSaveStatus = 'idle',
  loading = false,
  titleButtonContinue,
}) => {
  const { t } = useTranslation();
  const { getFeatureValue } = useFeatureFlags();
  const { user } = useSelector((state) => state.auth);
  const isSaving = autoSaveStatus === 'saving';

  const useTipsCreateVoice = getFeatureValue(FEATURE_KEYS.TIPS_CREATE_VOICE, {
    userId: user?.id,
    email: user?.email,
    phoneNumber: user?.phoneNumber,
  });

  return (
    <StyledFooterActions>
      <Box
        maxWidth="1280px"
        padding="0 40px"
        display="flex"
        boxSizing="border-box"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
      >
        <LeftActions>
          {useTipsCreateVoice && (
            <CommonButton
              variant="outlined"
              onClick={handleOpenTipsList}
              startIcon={
                <TipsAndUpdatesRoundedIcon
                  sx={{
                    width: '24px',
                    height: '24px',
                    color: COLOR.indigo[80],
                  }}
                />
              }
              textProps={{
                variantText: 'body-14-semibold',
                color: COLOR.darkBlue,
              }}
              customStyle={{
                height: '40px',
                borderRadius: '8px',
                padding: '0 16px',
              }}
            >
              {t('tipsForBestResults')}
            </CommonButton>
          )}
          <CommonButton
            size="small"
            variant="outlined"
            onClick={onGuideClick}
            startIcon={
              <ImportContactsRoundedIcon
                sx={{ width: '24px', height: '24px', color: COLOR.indigo[80] }}
              />
            }
            textProps={{
              variantText: 'body-14-semibold',
              color: COLOR.darkBlue,
            }}
            customStyle={{
              height: '40px',
              borderRadius: '8px',
              padding: '0 16px',
            }}
          >
            {t('instructions')}
          </CommonButton>
        </LeftActions>
        <RightActions>
          {showAutoSave && (
            <AutoSaveWrapper>
              {isSaving ? (
                <CircularProgress
                  size={18}
                  thickness={5}
                  sx={{ color: COLOR.indigo[64] }}
                />
              ) : (
                <CheckRoundedIcon
                  sx={{ width: 24, height: 24, color: COLOR.indigo[64] }}
                />
              )}
              <Typography variant="body-14-semibold" color={COLOR.indigo[64]}>
                {t('autoSave')}
              </Typography>
            </AutoSaveWrapper>
          )}
          <Tooltip
            title={continueTooltip}
            placement="left"
            arrow
            disableHoverListener={!continueTooltip || loading}
          >
            <span>
              <CommonButton
                variant="contained"
                onClick={onContinue}
                disabled={continueDisabled || loading}
                endIcon={
                  <NavigateNextRoundedIcon
                    sx={{
                      width: '24px',
                      height: '24px',
                      color: COLOR.indigo[80],
                    }}
                  />
                }
                startIcon={
                  loading ? (
                    <CircularProgress
                      size={16}
                      thickness={5}
                      sx={{ color: COLOR.indigo[80] }}
                    />
                  ) : null
                }
                textProps={{
                  variantText: 'body-14-bold',
                  color: COLOR.darkBlue,
                }}
                customStyle={{
                  height: '40px',
                  borderRadius: '8px',
                  padding: '0 16px 0 24px',
                }}
              >
                {titleButtonContinue || t('continue')}
              </CommonButton>
            </span>
          </Tooltip>
        </RightActions>
      </Box>
    </StyledFooterActions>
  );
};

export default FooterActions;
